import { useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, Banknote, Smartphone, CheckCircle2 } from "lucide-react";

/**
 * Iter 35 — Complete / Collect Remaining modal for "Booked via Other App" bookings.
 *
 * Fields:
 *   • Total Amount (static)           — booking.amount_to_collect
 *   • Advance Paid (static)           — booking.paid_amount (running collected total)
 *   • Balance Remaining (static)      — total − advance
 *   • Amount Collected Now (input)
 *   • Payment Mode (Cash / UPI)
 *   • Notes (optional)
 *
 * On Confirm → POST /api/bookings/{id}/external-collect.
 * Backend recomputes payment_status (paid / partial / pending) and sets status = "completed".
 */
export default function ExternalCompleteModal({ booking, onClose, onSaved }) {
    const total = Number(booking.amount_to_collect || 0);
    const advance = Number(booking.paid_amount || 0);
    const remaining = Math.max(0, total - advance);
    const [amount, setAmount] = useState(remaining);
    const [method, setMethod] = useState("cash");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const sourceLabel = (booking.external_source || "Other App").trim();
    const isFirstTime = (booking.collections?.length || 0) === 0;
    const title = isFirstTime ? "Complete Booking" : "Collect Remaining";

    const previewStatus = useMemo(() => {
        const newPaid = advance + Number(amount || 0);
        if (newPaid >= total && newPaid > 0) return { label: "Fully Paid", cls: "text-[#00FF66]" };
        if (newPaid > 0) return { label: "Partially Paid", cls: "text-amber-400" };
        return { label: "Unpaid", cls: "text-red-400" };
    }, [advance, amount, total]);

    const submit = async () => {
        const amt = Number(amount || 0);
        if (amt < 0) { toast.error("Amount cannot be negative"); return; }
        if (amt > remaining) { toast.error(`Amount cannot exceed remaining ₹${remaining}`); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/bookings/${booking.id}/external-collect`, {
                amount: amt, method, notes: notes.trim() || null,
            });
            const label = data.payment_status === "paid"
                ? "Fully paid — booking completed"
                : data.payment_status === "partial"
                    ? `Partial collected · ₹${data.remaining} remaining`
                    : "Booking completed · ₹0 collected";
            toast.success(label);
            onSaved && onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to record collection");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
            <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-5 my-6" data-testid="external-complete-modal">
                <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                        <h3 className="font-display text-xl font-bold uppercase text-white">{title}</h3>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="external-complete-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <p className="text-[11px] text-slate-500 mb-4">
                    {booking.booking_code} · {booking.user_name}
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-[10px] font-bold uppercase tracking-widest"
                        data-testid="external-source-badge-modal">via {sourceLabel}</span>
                </p>

                {/* Static info */}
                <div className="bg-slate-950 border border-slate-800 rounded p-3 space-y-1.5 mb-4">
                    <Row label="Total Amount" value={`₹${total.toLocaleString("en-IN")}`} testid="ext-total" />
                    <Row label="Advance Paid" value={`₹${advance.toLocaleString("en-IN")}`} testid="ext-advance" />
                    <div className="border-t border-slate-800 my-1" />
                    <Row label="Balance Remaining" value={`₹${remaining.toLocaleString("en-IN")}`} bold colorCls={remaining > 0 ? "text-amber-400" : "text-slate-500"} testid="ext-remaining" />
                </div>

                {/* Inputs */}
                <div className="space-y-3">
                    <div>
                        <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Amount Collected Now</label>
                        <input type="number" min="0" max={remaining} value={amount}
                            onChange={(e) => setAmount(Math.max(0, Math.min(remaining, Number(e.target.value || 0))))}
                            data-testid="ext-amount-input"
                            className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Payment Mode</label>
                        <div className="mt-1 grid grid-cols-2 gap-2">
                            <ModeBtn icon={Banknote} label="Cash" active={method === "cash"} onClick={() => setMethod("cash")} testid="ext-mode-cash" />
                            <ModeBtn icon={Smartphone} label="UPI" active={method === "upi"} onClick={() => setMethod("upi")} testid="ext-mode-upi" />
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Notes (optional)</label>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                            data-testid="ext-notes"
                            placeholder="e.g. customer requested receipt by email"
                            className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded text-white text-sm" />
                    </div>
                </div>

                <div className="mt-4 bg-slate-950 border border-slate-800 rounded p-2.5 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500">After this entry</span>
                    <span className={`text-sm font-bold ${previewStatus.cls}`} data-testid="ext-preview-status">{previewStatus.label}</span>
                </div>

                <div className="flex gap-3 pt-4">
                    <button onClick={onClose} className="flex-1 h-11 border border-slate-700 rounded text-slate-300">Cancel</button>
                    <button onClick={submit} disabled={submitting} data-testid="ext-confirm"
                        className="flex-1 h-11 bg-cyan-400 text-slate-950 font-bold uppercase tracking-widest rounded disabled:opacity-40">
                        {submitting ? "Saving..." : "Confirm"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, bold, colorCls, testid }) {
    return (
        <div className="flex items-center justify-between" {...(testid ? { "data-testid": testid } : {})}>
            <span className="text-xs uppercase tracking-widest text-slate-500">{label}</span>
            <span className={`${bold ? "font-bold text-base" : "text-sm"} ${colorCls || "text-white"}`}>{value}</span>
        </div>
    );
}

function ModeBtn({ icon: Icon, label, active, onClick, testid }) {
    return (
        <button onClick={onClick} data-testid={testid}
            className={`flex items-center justify-center gap-2 h-10 rounded border text-xs font-bold uppercase tracking-widest transition ${active ? "bg-cyan-400 text-slate-950 border-cyan-400" : "bg-slate-950 text-slate-300 border-slate-700 hover:border-cyan-700"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
        </button>
    );
}
